import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {EditorMmcModelService} from '@irb/irb-editors/editor-mmc-model/editor-mmc-model.service';
import {Subscription} from 'rxjs';
import {DeleteCellComponent} from '@irb-common/components/grid/renderers/delete.cell.component';
import {NumberTextBoxEditorComponent} from '@irb-common/components/grid/editors/number-text-box-editor.component';
import {CalltoactionService} from '@irb-common/components/calltoaction/calltoaction.service';
import {RequiredTextboxEditor} from '@irb-common/components/grid/editors/required-textbox-editor';
import {TooltipService} from '@irb-common/components/tooltip/tooltip.service';
import {UtilityService} from '@irb-common/services/utility.service';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {NumberTextBoxRendererComponent} from '@irb-common/components/grid/renderers/number-text-box.cell.component';
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import {DropdownFilterRendererComponent} from '@irb-common/components/grid/renderers/dropdown-filter.cell.component';
import {DropdownSearchMultipleRendererComponent} from '@irb-common/components/grid/renderers/dropdown-search-multiple.cell.component';

@Component({ standalone: false,
    selector: 'irb-mmc-background-cost-model',
    templateUrl: './mmc-background-cost-model.component.html',
    styleUrls: ['./mmc-background-cost-model.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MmcBackgroundCostModelComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('stratFeeTxt') stratFeeTxt: any;
    @ViewChild('suggestedMinTxt') suggestedMinTxt: any;
    @ViewChild('IrbDropdownAssetType') IrbDropdownAssetType: any;

    @Input() data: any;
    @Output() change = new EventEmitter<boolean>();
    @Output() validChange = new EventEmitter<any>();
    @Input() eventModelType: string;

    ddSub: Subscription;
    firmSub: Subscription;
    assetTypeOptions: any = [];
    assetType: any;
    grid: any;
    gridOptions: any;
    gridHeight: string = '145px';
    exceptionStrategistFeeList: any = [];
    listOfFirms: any;
    private passInit = false;
    stratFeeRowDisabled = false;
    frameworkComponents: any;
    isUiTest = false;
    disableFiled = true;

    constructor(private editorMmcModelService: EditorMmcModelService,
                private ctas: CalltoactionService,
                private tts: TooltipService,
                private util: UtilityService) {
    }

    ngOnInit(): void {

        this.firmSub = this.editorMmcModelService.getMMCFirms().subscribe((envms: any) => {
            let firms: any = [];
            if (envms) {
                for (const [key, value] of Object.entries(envms)) {
                    firms.push({name: value, value: key});
                }
            }
            const sortByKey = this.util.sortByKey;
            firms = sortByKey(firms, "name", 'asc');

            this.listOfFirms = firms || [];
        }, () => {
            this.listOfFirms = [];
        }, () => {

            this.initValues();
            this.getDropDownsInfo();
            this.ddSub = this.editorMmcModelService.dropDownsDataLoaded.subscribe(() => {
                this.getDropDownsInfo()
            });
        });

        this.frameworkComponents = {
            "DeleteCellComponent": DeleteCellComponent,
            "NumberTextBoxEditorComponent": NumberTextBoxEditorComponent,
            "RequiredTextboxEditor": RequiredTextboxEditor,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent,
            "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
            "DropdownSearchMultipleRendererComponent": DropdownSearchMultipleRendererComponent
        };
        this.gridOptions = {
            columnDefs: this.getColumns(),
            floatingFilter: false,
            overlayNoRowsTemplate: "<span></span>",
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this),
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
        };
        this.editorMmcModelService.dataSaved.subscribe((newData: any) => {
            this.exceptionStrategistFeeList = newData.exceptionStrategistFeeList;
            this.initValues();
            if (this.grid && this.grid.api) {
                this.grid.api.setGridOption('rowData', this.exceptionStrategistFeeList);
            }
        });
    }

    private initValues() {
        if (this.data) {
            this.disableFiled = this.data.usedFor === 'custom' ? false : true;
            if (this.data.updateStatus === "PROCESSING")
                this.disableFiled = true;
            if (this.data.assetType) {
                this.assetType = {value: this.data.assetType.id, name: this.data.assetType.label};
            }
            if (this.data.usedFor !== 'custom')
                this.exceptionStrategistFeeList = JSON.parse(JSON.stringify(this.data.exceptionStrategistFeeList));
            if (Array.isArray(this.exceptionStrategistFeeList) && this.exceptionStrategistFeeList.length !== 0) {
                this.exceptionStrategistFeeList.forEach((bcItem: any) => {
                    let defaultFirm = {name: bcItem.advisorFirmName, value: bcItem.advisorFirmName};
                    let isFound = false;
                    this.listOfFirms.forEach((firm: any) => {
                        if (firm.name === bcItem.advisorFirmName) {
                            defaultFirm.value = firm.value;
                            isFound = true;
                        }
                    });
                    bcItem.defaultFirm = defaultFirm;
                    if (!isFound) {
                        this.listOfFirms.push(defaultFirm);
                    }

                    let masterNumArr: any = [];
                    if (bcItem.masterAccounts && bcItem.masterAccounts.length !== 0) {
                        bcItem.masterAccounts.forEach((rc: string) => {
                            masterNumArr.push({name: rc, value: rc});
                        });
                    }
                    bcItem.listOfMasterNumbers = [];
                    bcItem.defaultMasterNumbers = masterNumArr;
                });
            }

        }
        this.passInit = true;
    }

    ngAfterViewInit() {
        if (this.data.suggestedMinimum && this.suggestedMinTxt && this.suggestedMinTxt.inputField) {
            setTimeout(() => {
                this.suggestedMinTxt.onBlur();
            }, 100);
        }
    }

    private getDropDownsInfo() {
        const dd: any = this.editorMmcModelService.getDropDownItems();
        if (dd && dd.ASSET_TYPE) {
            const sortByKey = this.util.sortByKey;
            this.assetTypeOptions = this.convertToValueLabel(dd.ASSET_TYPE.items);
            const assetTypeNone = dd.ASSET_TYPE.items.find((item: any) => item.label.toUpperCase() === 'NONE');
            this.assetTypeOptions = sortByKey(this.assetTypeOptions, "name", 'asc');
            if (this.data.usedFor === 'custom' && assetTypeNone) {
                this.assetTypeOptions.unshift({
                    value: assetTypeNone.id,
                    name: 'None',
                    id: assetTypeNone.id,
                    label: assetTypeNone.label
                });
                if (!this.assetType)
                    this.onAssetTypeChange(this.assetTypeOptions[0])
            }
        }
    }

    private convertToValueLabel(items: Array<any>) {
        let newArray: Array<any> = [], newEl: any;
        items.forEach((item: any) => {
            item.label = item.label || '';
            if (item.label.toUpperCase() !== 'NONE') {
                newEl = {
                    value: item.id,
                    name: item.label
                };
                newArray.push(newEl);
            }
        });
        return newArray;
    }

    getAssetTypes(searchStr: string) {
        this.IrbDropdownAssetType.set(DropdownFilterComponent.getFilteredList(searchStr, this.assetTypeOptions));
    }

    onAssetTypeChange(newAsset: any) {
        this.assetType = newAsset;
        if (this.data && this.eventModelType === 'editMmcModel' && this.data.assetType) {
            this.data.assetType.id = newAsset.value;
            this.data.assetType.label = newAsset.name;
        } else {
            this.data.assetType = {
                id: newAsset.value,
                label: newAsset.name,
                isNew: true,
                isUIdeleted: false
            }
        }

        this.validateEntries();
    }


    validateEntries() {
        if (!this.passInit)
            return;

        let isValid: boolean = true;
        if (!this.stratFeeTxt) {
            return;
        }

        if (!this.stratFeeTxt.inputControl.disabled) {
            if (!this.stratFeeTxt.inputControl.valid) {
                isValid = false;
            }
        }

        if (isValid) {
            if (typeof this.assetType === 'undefined') {
                isValid = false;
            }
        }

        if (isValid) {
            this.stratFeeRowDisabled = false;
            isValid = this.validateStratFeesGrid();
            this.stratFeeRowDisabled = !isValid || this.data.usedFor === 'custom';
        }
        let stratFeeData: any = [];
        let numberOfItems = 0;
        if (this.exceptionStrategistFeeList && this.exceptionStrategistFeeList.length) {
            this.exceptionStrategistFeeList.forEach((sf: any) => {
                stratFeeData.push({
                    environmentId: sf.environmentId,
                    "advisorFirmName": sf.advisorFirmName,
                    "excepStrategFeeId": sf.excepStrategFeeId,
                    "isDeleted": sf.isDeleted,
                    "isModified": sf.isModified,
                    "modelMarketId": this.data.modelMarketId || null,
                    "masterAccounts": sf.masterAccounts,
                    "strategistFee": sf.strategistFee
                });
                if (!sf.isDeleted)
                    numberOfItems++;
            });

            if (isValid && numberOfItems > 1) {
                isValid = this.checkForStrategistFeeDuplicates();
                this.stratFeeRowDisabled = !isValid;
            }
        }

        this.data.exceptionStrategistFeeList = stratFeeData;//this.exceptionStrategistFeeList;
        this.change.emit();
        this.validChange.emit({type: 'backcost', valid: isValid});
    }

    private validateStratFeesGrid() {
        let isValid: boolean = true;
        if (this.exceptionStrategistFeeList.length !== 0) {
            this.exceptionStrategistFeeList.forEach((item: any) => {
                if (!item.advisorFirmName || !item.masterAccounts) {
                    isValid = false;
                }
            });
        }
        return isValid;
    }


    getColumns() {
        const columns: any = [
            {
                headerName: "",
                cellRenderer: "DeleteCellComponent",
                cellRendererParams: {
                    message: "Remove this exception strategist fee?",
                    note: "",
                    proceed: this.delete.bind(this),
                    cancel: this.cancelDelete.bind(this),
                    disableDelete: () => !this.data.isProcessing
                },
                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
                suppressColumnsToolPanel: true,
                lockPosition: true,
                suppressMovable: true
            },
            {
                headerName: 'Advisor Firm Name',
                minWidth: 100,
                maxWidth: 300,
                suppressMovable: true,
                cellClass: 'dropdownFilterCell',
                cellRenderer: 'DropdownFilterRendererComponent',
                cellRendererParams: (params: any) => {
                    return {
                        defaultValue: params.data.defaultFirm,
                        disabled: this.data.isProcessing,
                        values: this.listOfFirms,
                        onChange: (params: any, item: IDropdownItem) => {
                            this.onFirmDropdownChanged(params, item);
                            this.validateEntries();
                            // params.data.valueLabel = item.value;
                        }
                    };
                }
            },
            {
                headerName: 'Master Account',
                minWidth: 400,
                suppressMovable: true,
                cellClass: 'dropdownSearchMultipleCell',
                cellRenderer: 'DropdownSearchMultipleRendererComponent',
                cellRendererParams: (params: any) => {
                    return {
                        disabled: this.data.isProcessing,
                        defaultValue: params.data.defaultMasterNumbers,
                        values: params.data.listOfMasterNumbers,
                        width: 500,
                        onChange: (params: any, selectedArray: any) => {
                            if (selectedArray && selectedArray.length) {
                                let selectedNames: any = [];
                                selectedArray.forEach((item:any) => {
                                    selectedNames.push(item.name);
                                });
                                params.data.masterAccounts = selectedNames;
                                params.data.defaultMasterNumbers = selectedArray;
                                params.data.isModified = true;
                            } else {
                                params.data.masterAccounts = '';
                                params.data.defaultMasterNumbers = [];
                            }
                            this.validateEntries();
                        },
                        enableHiddenSearchResults: false,
                        enableEmptySearchTerm: true
                    };
                },
            },
            {
                headerName: 'Exception Strategist Fee',
                field: 'strategistFee',
                minWidth: 120,
                maxWidth: 160,
                suppressMovable: true,
                cellClass: 'numberTextBoxCell',
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 0,
                        max: 9999999,
                        places: 2,
                        autoSelect: true,
                        autoHeight: true,
                        addZeroIfDotFirst: true,
                        disabled: this.data.isProcessing,
                        onChange: (params: any, c: any) => {
                            if (params.data.strategistFee !== c.value && c.isValid) {
                                params.data.strategistFee = parseFloat(c.value);
                                params.data.isModified = true;
                                params.api.redrawRows({rowNodes: [params.node]});
                                this.validateEntries();
                            }
                        }
                    };
                }
            }
        ];
        if (!this.data.isPaidForModel) {
            columns.forEach((col: any) => {
                col.sortable = false;
                col.resizable = false;
            });
        }
        return columns;
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isDeleted;
    }

    onCellValueChanged() {
        this.validateEntries();
    }

    addStratFee() {
        const newRec: any = {
            "advisorFirmName": "",
            "excepStrategFeeId": null,
            "isDeleted": false,
            "isModified": true,
            "modelMarketId": this.data.modelMarketId || null,
            "masterAccounts": "",
            "strategistFee": 0
        };
        this.exceptionStrategistFeeList.push(newRec);
        this.grid.api.setGridOption('rowData', this.exceptionStrategistFeeList);
        this.validChange.emit({type: 'backcost', valid: false});
        this.stratFeeRowDisabled = true;

        let rowIdx: number = null;
        this.grid.api.forEachNode((node: any) => {
            rowIdx = node.rowIndex;
        });

        if (rowIdx !== null) {
            this.grid.api.startEditingCell({
                rowIndex: rowIdx,
                colKey: 'advisorFirmName'
            });
        }
    }

    delete(deletedEl: any) {
        let idxTodelete: number;
        if (deletedEl.excepStrategFeeId) {
            for (let i = 0; i < this.exceptionStrategistFeeList.length; i++) {
                if (this.exceptionStrategistFeeList[i].excepStrategFeeId === deletedEl.excepStrategFeeId) {
                    this.exceptionStrategistFeeList[i].isDeleted = true;
                    idxTodelete = i;
                    break;
                }
            }
        } else {
            for (let i = 0; i < this.exceptionStrategistFeeList.length; i++) {
                if (this.exceptionStrategistFeeList[i].advisorFirmName === deletedEl.advisorFirmName && this.exceptionStrategistFeeList[i].masterAccounts === deletedEl.masterAccounts) {
                    idxTodelete = i;
                    break;
                }
            }
            this.exceptionStrategistFeeList.splice(idxTodelete, 1);
        }
        this.grid.api.setGridOption('rowData', this.exceptionStrategistFeeList);
        this.validateEntries();
    }

    cancelDelete() {
        this.ctas.remove();
    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.setGridOption('rowData', this.exceptionStrategistFeeList);

        if (this.exceptionStrategistFeeList && this.exceptionStrategistFeeList.length) {
            this.grid.api.forEachNode((node: any) => {
                this.getMasterNumbersForEnvironment(node, false);
            });
        }
    }

    getMasterNumbersForEnvironment(node: any, reValidate: boolean) {
        let envId: any = node.data.environmentId;

        if (envId) {
            this.editorMmcModelService.getMasterNumbers(envId).subscribe((list: any) => {
                if (list && list.length) {
                    let listOfMasterNumbersForEnv: any = [];
                    list.forEach((code: any) => {
                        listOfMasterNumbersForEnv.push({name: code, value: code});
                    });
                    node.data.listOfMasterNumbers = listOfMasterNumbersForEnv;
                    this.grid.api.redrawRows({rowNodes: [node]});
                    if (reValidate) {
                        this.validateEntries();
                    }
                }
            });
        }
    }

    onFirmDropdownChanged(params: any, item: any) {
        params.data.advisorFirmName = item.name;
        params.data.environmentId = item.value ? parseInt(item.value, 10) : null;
        params.data.defaultFirm = item;
        params.data.defaultMasterNumbers = null;
        params.data.listOfMasterNumbers = [];
        params.data.masterAccounts = null;
        this.getMasterNumbersForEnvironment(params.node, true);
    }

    checkForStrategistFeeDuplicates() {
        let isValid = true;
        let rowName, rowMasterNumber, isMatch;

        const withOutDeleted = this.exceptionStrategistFeeList.filter((item: any) => !item.isDeleted);
        if (withOutDeleted.length < 2)
            return isValid;

        for (let i = 0; i < withOutDeleted.length; i++) {
            rowName = withOutDeleted[i].advisorFirmName;
            rowMasterNumber = withOutDeleted[i].masterAccounts || [];
            isMatch = false;
            for (let j = 1; j < withOutDeleted.length; j++) {
                if (i !== j) {
                    let withOutDeletedRepCodes: any = withOutDeleted[j].masterAccounts || [];
                    isMatch = rowName === withOutDeleted[j].advisorFirmName && rowMasterNumber.join('') === withOutDeletedRepCodes.join('');

                    if (isMatch)
                        break;

                    if (rowName === withOutDeleted[j].advisorFirmName) {
                        isMatch = this.checkForDuplicatesMasters(rowMasterNumber, withOutDeletedRepCodes);
                        if (isMatch)
                            break;
                    }
                }
            }
            if (isMatch) {
                isValid = false;
                break;
            }
        }
        return isValid;
    }

    checkForDuplicatesMasters(arrayOfMasters1: any, arrayOfMasters2: any) {
        let isMatch = false;
        for (let k = 0; k < arrayOfMasters1.length; k++) {
            isMatch = false;
            let found =  arrayOfMasters2.find((element: string) => arrayOfMasters1[k] === element) ;
            isMatch = found ? true : false;
            if (isMatch)
                break;
        }

        return isMatch
    }

    ngOnDestroy() {
        if (this.ddSub) {
            this.ddSub.unsubscribe();
        }

        if (this.firmSub)
            this.firmSub.unsubscribe();

    }
}
